import { GraviButton, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { Tooltip } from 'antd'
import dayjs from 'dayjs'
import type * as React from 'react'

import type { RecentActivityResponse } from '../../../../api/useFreight/types/dashboard'
import { useRecentActivity } from '../../../../api/useFreight/useDashboard'
import { getTimeDisplay } from '../../Invoices/useGridFilters'
import { useLaunchpadContext } from '../context'
import { ActivityContent } from './common/ActivityContent'
import { UserAvatar } from './common/UserAvatar'
import { formatters } from '@/utils/intl/dateTimeFormatters'

/**
 * Component that displays recent activity in the system
 */
export function ActivitySection() {
  // Use the API hook to get recent activity
  const { costRevenue } = useLaunchpadContext()
  const { data: recentActivity, isLoading, error } = useRecentActivity(costRevenue)

  if (isLoading) {
    return (
      <Vertical flex={1} borderRadius='6px' border='all' bg='elevated'>
        <Vertical flex='0' style={{ overflow: 'visible' }} className='p-3' bg='default'>
          <Texto category='p2'>Recent Activity</Texto>
        </Vertical>
        <Vertical flex={1} verticalCenter horizontalCenter bg='default' className='p-3'>
          <Texto>Loading recent activity...</Texto>
        </Vertical>
      </Vertical>
    )
  }

  const activities = recentActivity || []

  return (
    <Vertical flex={1} borderRadius='6px' border='all' bg='elevated'>
      <Vertical flex='0' style={{ overflow: 'visible' }} className='p-3' bg='default'>
        <Texto category='p2'>Recent Activity</Texto>
      </Vertical>
      <Vertical flex={1} style={{ overflow: 'auto' }} bg='default' className='p-3 ' gap={12}>
        {error ? (
          <Texto align='center' appearance='error'>
            Error loading recent activity
          </Texto>
        ) : activities.length > 0 ? (
          activities.map((activity) => (
            <ActivityItem key={activity._id} activity={activity} costRevenue={costRevenue} />
          ))
        ) : (
          <Texto align='center' appearance='hint'>
            No recent activity found
          </Texto>
        )}
      </Vertical>
    </Vertical>
  )
}

/**
 * Individual activity item component
 */
function ActivityItem({
  activity,
  costRevenue,
}: { activity: RecentActivityResponse; costRevenue: 'Cost' | 'Revenue' }) {
  return (
    <Vertical flex='0' style={{ overflow: 'visible' }} className='pb-4 mb-3' border='bottom' gap={8}>
      <Horizontal gap={12} style={{ overflow: 'visible', width: '100%' }} verticalCenter>
        <UserAvatar username={activity.username} size={32} />
        <Horizontal style={{ overflow: 'visible' }} flex={1} gap={4} verticalCenter>
          <Vertical>
            <Texto style={{ whiteSpace: 'nowrap' }} weight='bold'>
              {activity.username}
            </Texto>

            <Horizontal gap={4} verticalCenter>
              {activity.extra_data?.group_number && (
                <Tooltip title='View Invoice'>
                  <GraviButton
                    className='p-0'
                    appearance='link'
                    onClick={() => {
                      window.open(`invoices/${activity.extra_data?.invoice_group_id}`, '_blank')
                    }}
                    style={{
                      textTransform: 'uppercase',
                      height: 16,
                      textDecoration: 'underline',
                      fontSize: 12,
                    }}
                    theme2
                  >
                    {activity.extra_data?.group_number}
                  </GraviButton>
                </Tooltip>
              )}
            </Horizontal>
          </Vertical>
        </Horizontal>
        <Vertical flex='0' style={{ overflow: 'visible' }} alignItems='flex-end'>
          <Texto style={{ whiteSpace: 'nowrap' }} category='label'>
            {formatters.SHORT_DATE_TIME(activity.created_on)}
          </Texto>
          <Texto style={{ whiteSpace: 'nowrap' }} category='label' appearance='hint'>
            {getTimeDisplay(activity.created_on)?.display}
          </Texto>
        </Vertical>
      </Horizontal>

      <ActivityContent type={activity.activity_type} extraData={activity.extra_data} costRevenue={costRevenue} />
    </Vertical>
  )
}
